import {
  Icon,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiClose, mdiDownload, mdiLoading } from "@mdi/js";
import {
  Alert,
  AppBar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  type DialogProps,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
} from "@mui/material";
import { blue } from "@mui/material/colors";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { MaintenanceWindow } from "../../../../../@types/services/MaintenanceWindow";
import type { MaterialRequestForm } from "../../../../../@types/services/MaterialRequestForm";
import { CommonErrorHelp } from "../../../../../components/molecules/CommonErrorHelp";
import { ApiErrorsTable } from "../../../../../components/organisms/ApiErrorsTable";
import { ForbiddenError } from "../../../../../errors/ForbiddenError";
import { useAuth } from "../../../../../hooks/useAuth";
import { fetchMaintenanceWindows } from "../../../../../services/maintenanceWindows";
import {
  fetchMaterialRequestForm,
  uploadMaterialRequestFormAttach,
} from "../../../../../services/materialRequestForms";

const loaderQueryOptions = (materialRequestFormId: string) =>
  queryOptions({
    queryFn: () => fetchMaterialRequestForm(materialRequestFormId),
    queryKey: ["materialRequestForms", "detail", materialRequestFormId],
    staleTime: 1000 * 60 * 5,
  });

interface MassOffer extends DialogProps {
  materialRequestFormId: string;
}

const MassOfferDialog: React.FC<MassOffer> = ({
  materialRequestFormId,
  open,
  onClose,
}) => {
  const router = useRouter();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [attach, setAttach] = useState<File | undefined>();

  const updateAttach = useCallback(() => {
    setLoading(() => true);

    if (!attach) {
      return;
    }

    const formData = new FormData();
    formData.append("attachment", attach);

    uploadMaterialRequestFormAttach(materialRequestFormId, formData)
      .then(() => {
        router.invalidate();
        enqueueSnackbar("Arquivo enviado com sucesso!");
        if (onClose) {
          onClose({}, "backdropClick");
        }
      })
      .catch(() => {
        enqueueSnackbar("Não foi possível enviar o arquivo!");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [attach, enqueueSnackbar, materialRequestFormId, onClose, router]);

  return (
    <Dialog onClose={onClose} open={open}>
      <DialogTitle>Mass Offer</DialogTitle>
      <DialogContent>
        <div
          style={{
            alignItems: "center",
            display: "flex",
            gap: "5px",
            justifyContent: "space-between",
          }}
        >
          <DialogContentText>
            Baixe o template, preencha e envie
          </DialogContentText>
          <Button
            href={`/vbuyer/api/v2/material_request_forms/${materialRequestFormId}/mass_offer_template`}
            target="_blank"
          >
            Baixar template
          </Button>
        </div>

        <div
          style={{
            alignItems: "center",
            display: "flex",
            gap: "5px",
            justifyContent: "space-between",
            marginTop: "10px",
          }}
        >
          <input
            accept="xml"
            onChange={(e) => setAttach(() => e.target.files?.[0])}
            style={{
              border: "1px solid black",
              borderRadius: "2px",
              padding: "5px",
            }}
            type="file"
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose?.({}, "backdropClick")}>Cancel</Button>
        <Button
          disabled={!attach}
          loading={loading}
          loadingPosition="center"
          onClick={() => updateAttach()}
          variant="contained"
        >
          Enviar arquivo
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface MaterialRequestFormApiErrorDialogProps extends DialogProps {
  apiErrors: MaterialRequestForm["api_errors"];
}
const MaterialRequestFormApiErrorDialog: React.FC<
  MaterialRequestFormApiErrorDialogProps
> = ({ apiErrors, open, onClose }) => {
  return (
    <Dialog fullScreen onClose={onClose} open={open}>
      <AppBar sx={{ position: "relative" }}>
        <Toolbar>
          <IconButton
            aria-label="close"
            color="inherit"
            edge="start"
            onClick={() => onClose?.({}, "backdropClick")}
          >
            <Icon icon={mdiClose} />
          </IconButton>
          <Typography component="div" sx={{ flex: 1, ml: 2 }} variant="h6">
            Generate Purchase Order Errors
          </Typography>
        </Toolbar>
      </AppBar>

      <DialogContent>
        <ApiErrorsTable apiErrors={apiErrors} />
      </DialogContent>
    </Dialog>
  );
};

const ShowMaterialRequestForm: React.FC = () => {
  const { t } = useTranslation();

  const { materialRequestFormId } = Route.useParams();
  const { data: materialRequestForm } = useSuspenseQuery(
    loaderQueryOptions(materialRequestFormId),
  );

  const [maintenanceWindows, setMaintenanceWindows] = useState<
    MaintenanceWindow[]
  >([]);
  const [open, setOpen] = useState(false);
  const [apiErrorsOpen, setApiErrorsOpen] = useState(false);
  const [loadingGeneratePurchaseOrder, setLoadingGeneratePurchaseOrder] =
    useState(false);
  const { user } = useAuth();

  const didInit = useRef(false);

  const hasMaintenanceWindows = maintenanceWindows.length > 0;

  const handleClickOpen = useCallback(() => {
    setOpen(() => true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(() => false);
  }, []);

  const handleApiErrorsClickOpen = useCallback(() => {
    setApiErrorsOpen(() => true);
  }, []);

  const handleApiErrorsClose = useCallback(() => {
    setApiErrorsOpen(() => false);
  }, []);

  const handleFetchMaintenanceWindows = useCallback(async () => {
    const response = await fetchMaintenanceWindows({
      filters: { active: true, system: "sap_p03" },
    });
    setMaintenanceWindows(
      response.results.filter(
        (maintenanceWindow) =>
          maintenanceWindow.blocked_actions.length === 0 ||
          maintenanceWindow.blocked_actions.includes(
            "issue_material_purchase_order",
          ),
      ),
    );
  }, []);

  useEffect(() => {
    if (!didInit.current) {
      didInit.current = true;
      handleFetchMaintenanceWindows();
    }
  }, [handleFetchMaintenanceWindows]);

  return (
    <PageContainer
      maxWidth="xl"
      showBackButton
      title={t("materialRequestForm.title")}
    >
      <Box sx={{ marginBottom: 2 }}>
        {!loadingGeneratePurchaseOrder &&
          Object.keys(materialRequestForm.api_errors).length > 0 && (
            <>
              <Alert
                action={
                  <Button
                    color="error"
                    onClick={handleApiErrorsClickOpen}
                    variant="outlined"
                  >
                    View Errors
                  </Button>
                }
                severity="error"
              >
                The last attempt to generate the purchase order failed.
              </Alert>

              <MaterialRequestFormApiErrorDialog
                apiErrors={materialRequestForm.api_errors}
                onClose={handleApiErrorsClose}
                open={apiErrorsOpen}
              />

              <br />
              <CommonErrorHelp errorType="create_purchase_order_material_quotation" />
            </>
          )}

        {!materialRequestForm?.filled && (
          <Alert severity="info">
            {t("materialRequestForm.pendingFilling")}
          </Alert>
        )}

        {!!materialRequestForm?.purchase_order?.purchase_order_pdf_document
          ?.attachment && (
          <Button
            color="success"
            download={
              materialRequestForm.purchase_order.purchase_order_pdf_document
                .attachment.filename
            }
            href={`/vbuyer/purchase_orders/${materialRequestForm.purchase_order.id}/download.pdf`}
            startIcon={<Icon icon={mdiDownload} />}
            target="_blank"
            variant="outlined"
          >
            Download Purchase Order
          </Button>
        )}

        {(loadingGeneratePurchaseOrder ||
          (!materialRequestForm?.purchase_order &&
            materialRequestForm?.create_purchase_order_jid)) && (
          <Button
            color="info"
            startIcon={<Icon icon={mdiLoading} spin />}
            variant="outlined"
          >
            Generating Purchase Order
          </Button>
        )}

        {!loadingGeneratePurchaseOrder &&
          !materialRequestForm?.purchase_order &&
          !materialRequestForm?.create_purchase_order_jid &&
          materialRequestForm?.filled &&
          user?.equinor &&
          user?.roles_names.includes("Generate Purchase Order") && (
            <>
              {hasMaintenanceWindows &&
                maintenanceWindows.map((maintenanceWindow) => (
                  <Alert
                    key={maintenanceWindow.id}
                    severity="warning"
                    sx={{ mb: 2 }}
                  >
                    {maintenanceWindow.message}
                  </Alert>
                ))}
              <Button
                disabled={hasMaintenanceWindows}
                href={`/vbuyer/api/v2/material_request_forms/${materialRequestForm.id}/generate_purchase_order`}
                onClick={() => setLoadingGeneratePurchaseOrder(() => true)}
                variant="outlined"
              >
                Generate Purchase Order
              </Button>
            </>
          )}
        {!loadingGeneratePurchaseOrder &&
          !materialRequestForm?.purchase_order &&
          !materialRequestForm?.create_purchase_order_jid &&
          user?.roles_names.includes("Material Request Form - Mass Offer") && (
            <>
              <Button onClick={handleClickOpen} variant="outlined">
                Mass Offer
              </Button>

              <MassOfferDialog
                materialRequestFormId={materialRequestForm.id}
                onClose={handleClose}
                open={open}
              />
            </>
          )}
      </Box>

      <Box>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell align="left">Purchase Requisition</TableCell>
                <TableCell align="left">Purchase Requisition Item</TableCell>
                <TableCell align="left">Work Order</TableCell>
                <TableCell align="left">Purchase Order</TableCell>
                <TableCell align="left">Purchase Order Item</TableCell>
                <TableCell align="left">Quantity</TableCell>
                <TableCell align="left">Short Description</TableCell>
                <TableCell align="left">Unit Price</TableCell>
                <TableCell align="left">Lead Time</TableCell>
                <TableCell align="left">NCM</TableCell>
                <TableCell align="left">Tax Code</TableCell>
                <TableCell align="left">Local Content Percentage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {materialRequestForm &&
                materialRequestForm.material_request_form_items?.length > 0 &&
                materialRequestForm.material_request_form_items.map(
                  (materialRequestFormItem) => (
                    <TableRow
                      key={materialRequestFormItem.id}
                      sx={{
                        borderLeftColor: blue["500"],
                        borderLeftStyle: materialRequestFormItem.filled
                          ? "none"
                          : "solid",
                      }}
                    >
                      <TableCell>
                        {materialRequestFormItem.purchase_requisition.pr_number}
                      </TableCell>
                      <TableCell>
                        {
                          materialRequestFormItem.purchase_requisition
                            .item_number
                        }
                      </TableCell>
                      <TableCell>
                        {materialRequestFormItem.purchase_requisition
                          .account_assignment_category === "F" &&
                          materialRequestFormItem.purchase_requisition
                            .plant_maintenance_order &&
                          `${materialRequestFormItem.purchase_requisition.plant_maintenance_order.order_number} - ${materialRequestFormItem.purchase_requisition.plant_maintenance_order.description}`}
                      </TableCell>
                      <TableCell>
                        {materialRequestFormItem.purchase_order_item?.po_number}
                      </TableCell>
                      <TableCell>
                        {
                          materialRequestFormItem.purchase_order_item
                            ?.po_item_number
                        }
                      </TableCell>
                      <TableCell>
                        {materialRequestFormItem.purchase_order_item
                          ?.order_quantity ||
                          materialRequestFormItem.purchase_requisition
                            .open_quantity}
                      </TableCell>
                      <TableCell>
                        {
                          materialRequestFormItem.purchase_requisition?.material
                            ?.short_description
                        }
                      </TableCell>
                      <TableCell>
                        {
                          materialRequestFormItem.purchase_requisition
                            .unit_price
                        }{" "}
                        {
                          materialRequestFormItem.purchase_requisition.currency
                            .name
                        }
                      </TableCell>
                      <TableCell>{materialRequestFormItem.lead_time}</TableCell>
                      <TableCell>{materialRequestFormItem.ncm?.code}</TableCell>
                      <TableCell>
                        {materialRequestFormItem.tax_code?.name}
                      </TableCell>
                      <TableCell>
                        {materialRequestFormItem.local_content_percentage}
                      </TableCell>
                    </TableRow>
                  ),
                )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/material_request_forms/$materialRequestFormId/",
)({
  beforeLoad({ context }) {
    if (
      context.auth.user?.roles_names.includes("Material Request Form - View")
    ) {
      return;
    }

    throw new ForbiddenError();
  },
  component: ShowMaterialRequestForm,
  loader: ({ context, params: { materialRequestFormId } }) =>
    context.queryClient.ensureQueryData(
      loaderQueryOptions(materialRequestFormId),
    ),
});
